import CustomerServiceAPI from '@/api/customer-services.api'
import BankInfoAPI from '@/api/bank-info.api'
import BillingAPI from '@/api/billing.api'
import BuyerCallLogsAPI from '@/api/buyer-call-logs.api'

const EIN_REGEX = /\d{2}[-]\d{7}\b/ // eslint-disable-line

const state = {
  paymentMethods: [],
  bankInfo: {
    address: {
      state: null
    }
  },
  accountPayments: [],
  accountBalance: {},


  accountSummary: {},
  purchasedCalls: {},
  balanceHistory: []
}

const getters = {
  unverifiedPaymentMethods(state) {
    return state.paymentMethods.filter(pm => pm._Status === 'Unverified')
  },

  accountBillingConfiguration(state, getters, rootState, rootGetters) {
    return rootGetters['auth/accountDetails']?.billingConfiguration
  },

  buyerMinimumCreditCardChargeAmount(state, getters) {
    return getters['accountBillingConfiguration'].minimumCreditCardChargeAmount
  },

  buyerMaximumCreditCardChargeAmount(state, getters) {
    return getters['accountBillingConfiguration'].maximumCreditCardChargeAmount
  },

  sellerPendingBalance(state) {
    const result = state.accountSummary.pendingBalance + state.accountSummary.holdingBalance
    return result || 0
  }
}

const actions = {
  async addPaymentMethod(context, { accountId, details }) {
    const response = await CustomerServiceAPI.addPaymentMethod({
      accountId,
      details
    })
    return response.data
  },

  async fetchPaymentMethods({ commit }, { accountId }) {
    const response = await CustomerServiceAPI.getAccountPaymentMethods({
      accountId
    })

    commit('SET_ACCOUNT_PAYMENT_METHODS', response.data)

    return response.data
  },

  async setPaymentMethodAsDefault(context, { accountId, paymentMethodId }) {
    const response = await CustomerServiceAPI.setPaymentMethodAsDefault({
      accountId,
      paymentMethodId
    })
    return response.data
  },

  async removePaymentMethod(context, { accountId, paymentMethodId }) {
    const response = await CustomerServiceAPI.removePaymentMethod({
      accountId,
      paymentMethodId
    })   

    return response.data
  },


  async updateAutoRechargeSettings(
    context,
    { accountId, billingConfigId, autoRecharge, rechargeAmount, rechargeBalance }
  ) {
    const response = await CustomerServiceAPI.updateAutoRechargeSettings({
      accountId,
      billingConfigId,
      autoRecharge,
      rechargeAmount,
      rechargeBalance
    })
    return response.data
  },

  async submitKYCDocs(
    context,
    {
      accountId,
      paymentMethodId,
      ccFront,
      ccBack,
      photoId,
      billingConfigId
    }
  ) {
    const response = await CustomerServiceAPI.submitKYCDocs({
      accountId,
      paymentMethodId,
      ccFront,
      ccBack,
      photoId,
      billingConfigId
    })
    return response.data
  },

  async getBankInfo({ commit }, accountId) {
    const response = await BankInfoAPI.getBankInfo(accountId)

    if (response.data) {
      commit('SET_BANK_INFO', response.data)
    }

    return response.data
  },

  async getUnmaskedBankInfo({ commit }, accountId) {
    const response = await BankInfoAPI.getUnmaskedBankInfo(accountId)

    if (response.data) {
      commit('SET_BANK_INFO', response.data)
    }
    
    return response.data
  },

  setBankInfo({ commit }, bankInfo) {
    commit('SET_BANK_INFO', bankInfo)
  },

  async saveBankInfo({ rootGetters }, { bankInfo, mode = 'create' }) {
    let apiToCall
    
    bankInfo.address.zip = String(bankInfo.address.zip)

    if (mode === 'create') {
      apiToCall = BankInfoAPI.saveBankInfo
    } else {
      apiToCall = BankInfoAPI.updateBankInfo
      if (!bankInfo.accountNumber) {
        const unmaskedBankInfoResponse = await BankInfoAPI.getUnmaskedBankInfo(rootGetters['auth/accountId'])
        bankInfo.accountNumber = unmaskedBankInfoResponse.data.accountNumber
      }
    }

    const response = await apiToCall(
      rootGetters['auth/accountId'],
      bankInfo
    )

    return response.data
  },

  async getBuyerAccountBalance({ commit, rootGetters, dispatch }) {
    const response = await CustomerServiceAPI.getBuyerAccountBalance(
      rootGetters['auth/accountDetails']?.id
    )

    commit('SET_ACCOUNT_BALANCE', response.data)

    if (response.data.isBalanceLow) {
      dispatch('showDashboardNotification', {
        text: 'Your balance is too low to buy calls. ',
        linkText: 'Click here to add funds',
        routeName: 'AddFunds',
        hideOnClick: false
      }, { root:true })
    }

    if (response.data.isDailyBudgetReached) {
      dispatch('showDashboardNotification', {
        text: 'Your daily budget is exceeded. ',
        linkText: 'Click here to change account daily budget. ',
        routeName: 'BuyerAccountBudget',
        hideOnClick: false
      }, { root:true })
    }

    await dispatch('getAccountBillingSummary')

    return response.data
  },

  async getAccountBillingSummary({ commit, rootGetters }) {
    const response = await CustomerServiceAPI.getAccountBillingSummary(
      rootGetters['auth/accountDetails']?.id
    )

    commit('SET_ACCOUNT_SUMMARY', response.data)
    return response.data
  },

  async addFundsByCreditCardCharge(context, { amount, accountId, creditCardId }) {
    const response = await BillingAPI.addFundsByCreditCardCharge(accountId, amount, creditCardId)

    return response.data
  },
  
  async getAccountPayments({ rootGetters, commit }) {
    let accountId = rootGetters['auth/accountId']
    
    const response = await BillingAPI.getAccountPayments(accountId)
    commit('SET_ACCOUNT_PAYMENTS', response.data)
    return response.data
  },


  async fetchPurchasedCalls({ commit, rootGetters, state }) {
    const accountId = rootGetters['auth/accountId']
    const timeZone = rootGetters['auth/accountDetails']?.timeZoneId || 'UTC'
    
    const now = new Date()
    let fromDate

    // Find the most recent invoice end date
    if (state.accountPayments && state.accountPayments.length > 0) {
      // Assuming billingCycleEndDate is an ISO string or compatible
      const latestInvoice = state.accountPayments.reduce((latest, current) => {
        if (!current.billingCycleEndDate) return latest
        const currentDate = new Date(current.billingCycleEndDate)
        const latestDate = new Date(latest.billingCycleEndDate)
        return currentDate > latestDate ? current : latest
      }, state.accountPayments[0])

      if (latestInvoice && latestInvoice.billingCycleEndDate) {
        fromDate = new Date(latestInvoice.billingCycleEndDate)
      }
    }

    if (!fromDate) {
      // Fallback: Start of current month
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1)
      fromDate.setHours(0, 0, 0, 0)
    }

    const response = await BuyerCallLogsAPI.getBuyerTotalCalls(accountId, {
      from: fromDate,
      to: now,
      timeZone
    })
    
    commit('SET_CALLS_SUMMARY', response.data)
    return response.data
  },

  async fetchBalanceHistory({ commit, rootGetters }, { startDate, endDate } = {}) {
    const accountId = rootGetters['auth/accountId']
    const isBuyer = rootGetters['auth/isBuyerAccount']
    const accountType = isBuyer ? 'buyer' : 'seller'

    const response = await CustomerServiceAPI.getAccountBalanceHistory(accountId, { startDate, endDate, accountType })
    commit('SET_BALANCE_HISTORY', response.data)
    return response.data
  },
}

const mutations = {
  SET_ACCOUNT_PAYMENT_METHODS(state, paymentMethods) {
    state.paymentMethods = paymentMethods
  },

  SET_BANK_INFO(state, bankInfo) {
    state.bankInfo = bankInfo
  },

  SET_ACCOUNT_BALANCE(state, accountBalance) {
    state.accountBalance = accountBalance
  },

  SET_ACCOUNT_SUMMARY(state, accountSummary) {
    state.accountSummary = accountSummary
  },

  SET_ACCOUNT_PAYMENTS(state, payments) {
    state.accountPayments = payments
  },

  SET_BALANCE_HISTORY(state, history) {
    state.balanceHistory = history
  },

  SET_CALLS_SUMMARY(state, data) {
    state.purchasedCalls = {
      purchasedCallsAmount: data.totalCost || 0,
      purchasedCallsCount: data.totalCallCount || 0
    }
  },
}

export { EIN_REGEX }

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
